(() => {
  if (window.RavinArcDeckLoaded) return;
  window.RavinArcDeckLoaded = true;

  const instances = new WeakMap();
  const clamp = (value, low, high) => Math.max(low, Math.min(high, value));
  const modulo = (index, total) => ((index % total) + total) % total;
  const pad = value => String(value).padStart(2, '0');
  const ease = t => 1 - Math.pow(1 - t, 3);

  function init(root) {
    if (!root || instances.has(root)) return;
    const stage = root.querySelector('[data-ravin-arc-sticky]');
    const scene = root.querySelector('.ravin-arc__stage');
    const cards = Array.from(root.querySelectorAll('[data-ravin-arc-card]'));
    if (!stage || !scene || !cards.length) return;

    const current = root.querySelector('[data-ravin-arc-current]');
    const status = root.querySelector('[data-ravin-arc-status]');
    const prev = root.querySelector('[data-ravin-arc-prev]');
    const next = root.querySelector('[data-ravin-arc-next]');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobile = window.matchMedia('(max-width: 749px)');
    const controller = new AbortController();
    const spread = clamp(Number(root.dataset.ravinArcSpread) || 19, 14, 25);
    const tilt = clamp(Number(root.dataset.ravinArcTilt) || 14, 0, 30);
    const depth = clamp(Number(root.dataset.ravinArcDepth) || 160, 0, 300);
    const swipeEnabled = root.dataset.ravinArcSwipe === 'true';
    const duration = 530;
    const count = cards.length;
    let position = 0;
    let target = 0;
    let focus = -1;
    let frame = 0;
    let startedAt = 0;
    let from = 0;
    let to = 0;
    let touch = null;
    let pointer = null;
    let blockProductClickUntil = 0;

    function paint(card, x, y, z, rz, ry, rx, scale, alpha, layer) {
      const style = card.style;
      style.setProperty('--ravin-arc-x', x.toFixed(2) + 'px');
      style.setProperty('--ravin-arc-y', y.toFixed(2) + 'px');
      style.setProperty('--ravin-arc-z', z.toFixed(2) + 'px');
      style.setProperty('--ravin-arc-rz', rz.toFixed(2) + 'deg');
      style.setProperty('--ravin-arc-ry', ry.toFixed(2) + 'deg');
      style.setProperty('--ravin-arc-rx', rx.toFixed(2) + 'deg');
      style.setProperty('--ravin-arc-scale', scale.toFixed(4));
      style.setProperty('--ravin-arc-alpha', alpha.toFixed(3));
      style.zIndex = String(layer);
    }

    function render() {
      const focused = modulo(Math.round(position), count);
      const width = Math.max(1, scene.clientWidth);
      root.style.setProperty('--ravin-arc-progress', ((focused + 1) / count).toFixed(5));

      if (focused !== focus) {
        if (current) current.textContent = pad(focused + 1);
        cards.forEach((card, index) => {
          const selected = index === focused;
          card.classList.toggle('is-active', selected);
          card.toggleAttribute('inert', !selected);
          if (selected) card.removeAttribute('aria-hidden');
          else card.setAttribute('aria-hidden','true');
        });
        focus = focused;
      }

      const half = count / 2;
      cards.forEach((card, index) => {
        if (reduced.matches) {
          paint(card, 0, 0, depth, 0, 0, 0, 1, index === focused ? 1 : 0,
            index === focused ? 1000 : 1);
          return;
        }
        const relative = index - position;
        const offset = count > 1 ? relative - count * Math.round(relative / count) : 0;
        const d = Math.abs(offset);
        const x = offset * width * (mobile.matches ? .58 : spread / 100);
        const y = 0;
        const z = depth - d * (mobile.matches ? 145 : 110);
        const rz = offset * (mobile.matches ? 9 : 12);
        const ry = -offset * (mobile.matches ? tilt * 1.3 : tilt);
        const rx = -4;
        const scale = (mobile.matches ? 1.04 : 1.11) - d * (mobile.matches ? .12 : .09);
        const alpha = clamp((half - d) * 2.8, 0, 1);
        const layer = 1000 - Math.round(d * 100);
        paint(card, x, y, z, rz, ry, rx, scale, alpha, layer);
      });
    }

    function announce() {
      if (!status) return;
      const index = modulo(Math.round(target), count);
      const name = cards[index]?.querySelector('.ravin-arc__card-title')?.textContent?.trim() || 'Fashion card';
      status.textContent = name + ', card ' + (index + 1) + ' of ' + count;
    }

    function stopAnimation() {
      if (frame) window.cancelAnimationFrame(frame);
      frame = 0;
    }

    function navigate(step, immediate = false) {
      if (count <= 1) return;
      stopAnimation();
      target += step;
      announce();
      if (reduced.matches || immediate) {
        position = modulo(target, count);
        target = position;
        render();
        return;
      }
      from = position;
      to = target;
      startedAt = performance.now();
      const tick = now => {
        const progress = clamp((now - startedAt) / duration, 0, 1);
        position = from + (to - from) * ease(progress);
        render();
        if (progress < 1) frame = window.requestAnimationFrame(tick);
        else {
          frame = 0;
          target = modulo(target, count);
          position = target;
          render();
        }
      };
      frame = window.requestAnimationFrame(tick);
    }

    function selectCard(index) {
      stopAnimation();
      target = modulo(index,count);
      position = target;
      announce();
      render();
    }

    if (prev) prev.addEventListener('click',()=>navigate(-1),{signal:controller.signal});
    if (next) next.addEventListener('click',()=>navigate(1),{signal:controller.signal});

    stage.addEventListener('click',event => {
      if (Date.now() < blockProductClickUntil && event.target.closest('.ravin-arc__card-link')) {
        event.preventDefault();
        event.stopPropagation();
      }
    },{capture:true,signal:controller.signal});

    const findTouch = (list,id) => {
      for (const item of list) if (item.identifier === id) return item;
      return null;
    };

    if (swipeEnabled && count > 1) {
      stage.addEventListener('touchstart',event => {
        if (event.touches.length !== 1 || event.target.closest('button')) {
          touch=null;
          return;
        }
        const finger=event.touches[0];
        touch={id:finger.identifier,x:finger.clientX,y:finger.clientY,horizontal:false,vertical:false};
      },{passive:true,signal:controller.signal});

      stage.addEventListener('touchmove',event => {
        if (!touch || event.touches.length !== 1) {
          touch=null;
          return;
        }
        const finger=findTouch(event.touches,touch.id);
        if (!finger) return;
        const dx=finger.clientX-touch.x,dy=finger.clientY-touch.y;
        if (!touch.horizontal && !touch.vertical && Math.abs(dy) > 11 &&
            Math.abs(dy) > Math.abs(dx)*1.16) touch.vertical=true;
        if (touch.vertical) return;
        if (!touch.horizontal && Math.abs(dx) > 11 &&
            Math.abs(dx) > Math.abs(dy)*1.2) touch.horizontal=true;
        if (touch.horizontal && event.cancelable) event.preventDefault();
      },{passive:false,signal:controller.signal});

      stage.addEventListener('touchend',event => {
        if (!touch) return;
        const finger=findTouch(event.changedTouches,touch.id);
        if (!finger) return;
        const start=touch;
        touch=null;
        const dx=finger.clientX-start.x,dy=finger.clientY-start.y;
        if (start.vertical || Math.abs(dx) < 38 || Math.abs(dx) <= Math.abs(dy)*1.2) return;
        blockProductClickUntil=Date.now()+550;
        navigate(dx < 0 ? 1 : -1);
      },{passive:true,signal:controller.signal});
      stage.addEventListener('touchcancel',()=>{touch=null;},{passive:true,signal:controller.signal});

      stage.addEventListener('pointerdown',event => {
        if (event.pointerType === 'touch' || event.button !== 0 || event.target.closest('button')) return;
        pointer={id:event.pointerId,x:event.clientX,y:event.clientY};
      },{signal:controller.signal});
      stage.addEventListener('pointerup',event => {
        if (!pointer || pointer.id !== event.pointerId) return;
        const start=pointer;
        pointer=null;
        const dx=event.clientX-start.x,dy=event.clientY-start.y;
        if (Math.abs(dx) < 38 || Math.abs(dx) <= Math.abs(dy)*1.2) return;
        blockProductClickUntil=Date.now()+550;
        navigate(dx < 0 ? 1 : -1);
      },{signal:controller.signal});
      for (const type of ['pointercancel','lostpointercapture']) {
        stage.addEventListener(type,()=>{pointer=null;},{signal:controller.signal});
      }
    }

    root.addEventListener('keydown',event => {
      if (!['ArrowLeft','ArrowRight'].includes(event.key) ||
        event.target.closest('input,textarea,select,[contenteditable="true"]')) return;
      event.preventDefault();
      navigate(event.key==='ArrowRight' ? 1 : -1);
    },{signal:controller.signal});

    root.addEventListener('shopify:block:select',event => {
      const card=event.target.closest('[data-ravin-arc-card]');
      if (!card || !root.contains(card)) return;
      const index=cards.indexOf(card);
      if (index>=0) selectCard(index);
    },{signal:controller.signal});

    window.addEventListener('resize',render,{passive:true,signal:controller.signal});
    window.addEventListener('pageshow',render,{signal:controller.signal});
    reduced.addEventListener?.('change',()=>{
      stopAnimation();
      target=modulo(Math.round(target),count);
      position=target;
      render();
    },{signal:controller.signal});

    instances.set(root,()=>{
      stopAnimation();
      controller.abort();
      instances.delete(root);
    });
    render();
  }

  function initIn(scope) {
    if (scope.matches?.('[data-ravin-3d-arc-deck]')) init(scope);
    scope.querySelectorAll?.('[data-ravin-3d-arc-deck]').forEach(init);
  }
  function unloadIn(scope) {
    const all=[];
    if (scope.matches?.('[data-ravin-3d-arc-deck]')) all.push(scope);
    scope.querySelectorAll?.('[data-ravin-3d-arc-deck]').forEach(x=>all.push(x));
    all.forEach(x=>instances.get(x)?.());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded',()=>initIn(document),{once:true});
  } else initIn(document);
  document.addEventListener('shopify:section:load',event=>initIn(event.target));
  document.addEventListener('shopify:section:unload',event=>unloadIn(event.target));
})();